/**
 * The complete permission catalogue for the platform.
 *
 * Per FD `business-rules.md § permission catalogue`. Declared up front
 * across M3-M10 so the library API is stable; M3 only USES the auth
 * subset (workspace/member/invitation/api-key), but downstream units
 * (M7 agents, M8 KB, M9 handoff, M10 surfaces) consume the rest without
 * needing a library bump.
 *
 * Slug format: `<subject>:<action>` (lowercase, kebab-case).
 *
 * Renaming a slug is a breaking change — old JWTs would fail
 * authorization. NEVER rename; deprecate + introduce a new slug if a
 * concept needs renaming.
 */
export const PERMISSIONS = [
    // Workspace administration (M3)
    "workspace:read",
    "workspace:update",
    // Member management (M3)
    "member:read",
    "member:invite",
    "member:update-role",
    "member:remove",
    // Invitation lifecycle (M3)
    "invitation:read",
    "invitation:revoke",
    // API keys (M3)
    "api-key:read",
    "api-key:create",
    "api-key:revoke",
    // Widget signing keys (contacts unit; replaces the role-based
    // AdminRoleGuard). Joins the derived `admin` bundle automatically; an admin
    // may grant it to a custom role (deliberate: permission-based delegation,
    // not role identity).
    "signing-key:manage",
    // Agents (M7 — declared now, used later)
    "agent:read",
    "agent:create",
    "agent:update",
    "agent:delete",
    // Knowledge base (M8 — KB unit PR-0 adds update/sync/attach)
    "kb:read",
    "kb:upload",
    "kb:update",
    "kb:delete",
    "kb:sync",
    "kb:attach",
    // Runs (M9 + M10)
    "run:read",
    // Human handoff (M9)
    "handoff:read",
    "handoff:take-over",
    // Reply to ANY handed-off chat, even one owned by another operator. Without
    // it an operator can only reply to chats they own (claimedByUserId) —
    // ownership is the implicit "reply-own", this slug is the "reply-all"
    // override (admins / desk leads; grantable to custom desk roles).
    "handoff:reply-all",
    "handoff:claim",
    "handoff:resolve",
    "handoff:handback",
    "handoff:configure",
    // Human-handoff decisions (M9 — request_decision / ruling). NB:
    // `decision:configure` was removed 2026-07-16 — declared early for a
    // decision-type authoring surface that never shipped, so it rendered as a
    // grantable checkbox that gated nothing. Re-append it when that surface
    // ships (append-only makes that safe).
    "decision:rule",
    // Conversations. `monitor` = watch the live queue / handoff board;
    // `read` = open and read a customer conversation's transcript. Split off
    // the overloaded `run:read` (which now means agent-run traces only) so a
    // desk role can grant "open a chat" without granting AgentOps. `monitor`
    // was formerly the misleadingly-named `session:monitor`.
    "conversation:monitor",
    "conversation:read",
    // Data tables (data-tables unit — declared now, used later)
    "data-table:read",
    "data-table:create",
    "data-table:update",
    "data-table:delete",
    // Response feedback (FDE technical feedback + reason catalogue)
    "feedback:read",
    "feedback:create",
    "feedback:manage",
    // Organization administration (B1 — RBAC depth). Append-only: never
    // reorder, never rename. `org:manage` rolls up SSO + org settings.
    "org:read",
    "org:manage",
    "org-member:read",
    "org-member:invite",
    "org-member:update-role",
    "org-member:remove",
    // Billing (B1)
    "billing:read",
    "billing:manage",
    // Workspace lifecycle at the org tier (B1)
    "workspace:create",
    // Platform / internal (B1) — CometChat staff cross-tenant support access.
    "platform:staff",
    // Runs — completes the phantom fixed in this PR (rename / card-submit
    // were gated on a slug that never existed).
    "run:update",
    // Workflows (rbac-permissions PR 1) — un-borrowed from agent:*
    "workflow:read",
    "workflow:create",
    "workflow:update",
    "workflow:delete",
    "workflow:publish",
    "workflow:run",
    "workflow:signal",
    "workflow:cancel",
    "workflow:run-as",
    // Stored queries (rbac-permissions PR 1) — un-borrowed from data-table:*
    "query:read",
    "query:create",
    "query:update",
    "query:delete",
    "query:run",
    // Internal notes (helpdesk) — operator-private annotations on a conversation.
    // Never shown to the customer or the AI; distinct from replying (take-over).
    "handoff:note",
    // Helpdesk team delegation (helpdesk-team unit). Grants a `helpdesk_admin`
    // scoped management of the desk WITHOUT workspace `member:*` / role-admin
    // powers: invite/assign/remove members holding HELPDESK-scoped roles only,
    // and author custom roles bounded by the helpdesk ceiling. Enforced by the
    // dedicated `/helpdesk/*` endpoints (scope baked into the route).
    "helpdesk:manage-members",
    "helpdesk:manage-roles",
    // Read-only view of the desk roster — every helpdesk operator (member AND
    // admin) can SEE who's on the desk; only `manage-members` can change it.
    "helpdesk:read-members",
];
const PERMISSION_SET = new Set(PERMISSIONS);
/**
 * Type-guard: is `slug` a member of the permission catalogue?
 *
 * Backs the `RoleDefinition` subset validation (custom roles draw only
 * from the catalogue) and lets the role resolver tolerate a slug retired
 * in code without throwing.
 */
export function isPermission(slug) {
    return PERMISSION_SET.has(slug);
}
/**
 * Maps a permission slug `<subject>:<action>` to its parts.
 *
 * Used by the CASL ability factory and the `@CheckAbility` decorator.
 */
export function parsePermission(perm) {
    const idx = perm.indexOf(":");
    // Caught by the const tuple type at compile time; runtime check is
    // belt-and-braces for the rare case of dynamic input.
    if (idx < 0) {
        throw new Error(`Invalid permission slug: ${perm}`);
    }
    return {
        subject: perm.slice(0, idx),
        action: perm.slice(idx + 1),
    };
}
//# sourceMappingURL=permissions.js.map